import { apiFetch } from '../auth/account.js';
import { mascotSVG } from '../cat/cat.js';
import { dbPut } from '../core/db.js';
import { cardsOfCourse } from '../core/load.js';
import { state } from '../core/state.js';
import { $, $$, esc } from '../core/text.js';
import { streakDays } from '../progress/streak.js';
import { ACHIEVEMENTS, IC, buddyMarkup, levelInfo } from '../progress/xp.js';
import { pushCloud } from '../sync/sync.js';
import { modal } from '../ui/modal.js';
import {
  fehlerBox, ladend, skelBestenliste, skelMeineDecks, skelProfilPopup,
  wireErneut, zeigeInhalt, zeigeSkeleton,
} from '../ui/skeleton.js';
import { toast } from '../ui/toast.js';
import { cloudMyDecks, cloudSetUsername } from './api.js';
import { usernameProblem } from './usernames.js';

/* ══════════ Profil (Discord-Stil: direkt am Profil bearbeiten) ══════════ */
export const saveProfile = () => dbPut('meta', { key: 'profile', value: state.profile });

/* ── Zwei Namen, die zusammengehören ─────────────────────────────────────
   Ein Konto hat zwei:

     `username`      – die Kennung. Einmalig, darunter wird man gefunden.
     `profile.name`  – der Anzeigename. Steht groß im Profil und in der
                       Bestenliste.

   Niemand hat sie je zusammengehalten. Wer seinen Nutzernamen änderte, sah
   im öffentlichen Profil weiter den alten Anzeigenamen – bei Joey einen, der
   noch aus einem ganz anderen Konto stammte. Von außen sieht das aus, als
   wären die Konten verbunden; tatsächlich war es nur ein Feld, das niemand
   nachzog.

   Deshalb diese Stelle: der Anzeigename folgt dem Nutzernamen, solange er
   abgeleitet ist. Wer ihn am Profil selbst geändert hat (`nameAuto` ist dann
   weg), behält ihn – seinen bewusst gewählten Namen still zu überschreiben
   wäre schlimmer als zwei verschiedene. */
export async function uebernehmeNutzername(neu) {
  if (!neu) return;
  if (state.me) state.me.username = neu;
  const p = state.profile;
  if (p.name && p.nameAuto === false) return;   // selbst gewählt: nicht anfassen
  p.name = neu;
  p.nameAuto = true;
  await saveProfile();
  pushCloud();   // damit die Bestenliste den neuen Namen bekommt
}
export const CONTEXTS = ['Schule', 'Hochschule', 'Universität', 'Ausbildung', 'Privat'];
export const pencil = (field) => `<button class="pf-pen" data-edit="${field}" title="Bearbeiten" aria-label="Bearbeiten"><img class="pen-ico" src="assets/icon_pencil.png" alt=""></button>`;

export function renderProfilePreview() {
  const p = state.profile;
  const info = levelInfo(state.progress.xp);
  $('#prof-avatar').innerHTML = buddyMarkup('happy');

  /* Anzeigename groß, Kennung klein daneben.

     Vorher stand hier nur der Anzeigename – und weil den niemand mit dem
     Nutzernamen zusammenhielt, konnte dort ein Name aus einem ganz anderen
     Konto stehen, ohne dass man erkannte, woher er kam. Die Kennung dazu
     macht sichtbar, welches Konto man gerade ansieht. */
  const kennung = state.me?.username
    ? `<span class="prof-handle" title="Dein Nutzername – darunter finden dich andere">@${esc(state.me.username)}</span>` : '';
  $('#prof-name').innerHTML = `<span class="pf-val">${esc(p.name || state.me?.username || 'Dein Name')}</span>${pencil('name')}${kennung}`;

  const metaItem = (field, label, val) =>
    `<span class="pf-chip"><span class="pf-k">${label}</span><span class="pf-val">${esc(val || '—')}</span>${pencil(field)}</span>`;
  $('#prof-meta').innerHTML =
    metaItem('age', 'Alter', p.age ? p.age + ' J.' : '') +
    metaItem('context', 'Kontext', p.context) +
    metaItem('school', p.context === 'Schule' ? 'Schule' : 'Einrichtung', p.school);

  $('#prof-bio').innerHTML = `<span class="pf-val">${p.bio ? esc(p.bio) : '<i>Schreib ein, zwei Sätze über dich…</i>'}</span>${pencil('bio')}`;

  $('#prof-badges').innerHTML =
    `<span class="prof-chip"><img class="chip-ico" src="${IC('trophy')}" alt="">Level ${info.level}</span>
     <span class="prof-chip"><img class="chip-ico" src="${IC('fire')}" alt="">${streakDays()} Tage</span>
     <span class="prof-chip"><img class="chip-ico" src="${IC('cards')}" alt="">${state.cards.length} Karten</span>`;

  const unlocked = ACHIEVEMENTS.filter((a) => state.progress.unlocked.includes(a.id));
  $('#prof-ach').innerHTML = unlocked.length
    ? unlocked.map((a) => `<img class="prof-ach-ico" src="${IC(a.icon)}" alt="" title="${esc(a.name)}">`).join('')
    : '<p class="page-sub">Noch keine Erfolge – leg los ✦</p>';

  renderLeaderboard();
  // Katzenzimmer aus dem öffentlichen Profil entfernt.

  const pub = state.courses.filter((c) => c.public && !c.archived).sort((a, b) => (b.created || 0) - (a.created || 0));
  $('#prof-decks').innerHTML = pub.length
    ? pub.map((c) => `<div class="prof-deck">
        <img class="pd-ico" src="${IC('folder')}" alt="">
        <div class="pd-body"><div class="pd-title">${esc(c.emoji ? c.emoji + ' ' : '')}${esc(c.name)}</div>
          <div class="pd-sub">${cardsOfCourse(c.id).length} Karten · seit ${new Date(c.created).toLocaleDateString('de')}</div></div>
        <div class="pd-likes">♥ –</div></div>`).join('')
    : '<p class="page-sub">Du hast noch keinen Kurs öffentlich gestellt – das geht in „Kurs bearbeiten".</p>';
  wireProfileEditors();
}

/* ══════════ Echte Bestenliste (alle Nutzer, nach XP / Karten / Streak) ══════════ */
export const cloudLeaderboard   = (sort) => apiFetch('/leaderboard?sort=' + (sort || 'xp'));
export const cloudPublicProfile = (id) => apiFetch('/profile/public?id=' + encodeURIComponent(id));
export const CAT_NAMES = { orange: 'Orange', grey: 'Grau', cream: 'Creme', night: 'Nacht' };
/* Katzen-Avatar einer beliebigen Person (Farbe + Accessoire) */
export function catAvatar(catColor, accessory, cls = '') {
  return `<div class="lb-cat ${cls}">${mascotSVG('happy', accessory || null, catColor || 'orange')}</div>`;
}
export const lbMetric = (e, sort) => sort === 'rated' ? `${e.rated} Karten` : sort === 'streak' ? `${e.streak} 🔥` : `${e.xp} XP`;

export async function renderLeaderboard() {
  const el = $('#prof-leaderboard'); if (!el) return;
  $$('#view-profile .lb-tab').forEach((b) => b.classList.toggle('active', b.dataset.lb === state.lbSort));
  /* Treppchen und Zeilen sind hoch – als Textzeile „Lädt…" klappte die halbe
     Seite zusammen und sprang dann wieder auf. */
  zeigeSkeleton(el, skelBestenliste(6));
  try {
    const { entries, myRank, sort } = await cloudLeaderboard(state.lbSort);
    ladend(el, false);
    if (!entries.length) { el.innerHTML = '<p class="page-sub">Noch niemand auf der Bestenliste – lern ein paar Karten und sei die/der Erste ✦</p>'; return; }
    const podium = entries.slice(0, 3);
    const rest = entries.slice(3);
    const order = [1, 0, 2];   // Silber · Gold · Bronze (Treppchen)
    const podiumHTML = `<div class="lb-podium">${order.filter((i) => podium[i]).map((i) => {
      const e = podium[i]; const medal = ['🥇', '🥈', '🥉'][i];
      return `<button class="lb-pod lb-pod-${i + 1} ${e.me ? 'me' : ''}" data-uid="${e.user_id}">
        <div class="lb-pod-medal">${medal}</div>
        ${catAvatar(e.cat_color, e.accessory, 'big')}
        <div class="lb-pod-name">${esc(e.name || e.username || 'Katze')}</div>
        <div class="lb-pod-val">${lbMetric(e, sort)}</div></button>`;
    }).join('')}</div>`;
    const rowsHTML = rest.map((e) => `<button class="lb-row ${e.me ? 'me' : ''}" data-uid="${e.user_id}">
        <span class="lb-rank">${e.rank}</span>
        ${catAvatar(e.cat_color, e.accessory)}
        <span class="lb-name">${esc(e.name || e.username || 'Katze')}<small>Level ${e.level}</small></span>
        <span class="lb-val">${lbMetric(e, sort)}</span></button>`).join('');
    const meOut = (myRank && !entries.some((e) => e.me))
      ? `<div class="lb-myrank">Dein Rang: <b>#${myRank}</b> – lern weiter, um aufzusteigen!</div>` : '';
    el.innerHTML = podiumHTML + `<div class="lb-list">${rowsHTML}</div>` + meOut;
    $$('#prof-leaderboard [data-uid]').forEach((b) => b.onclick = () => publicProfileModal(b.dataset.uid));
  } catch (e) {
    zeigeInhalt(el, fehlerBox('Die Bestenliste konnte nicht geladen werden: ' + e.message));
    wireErneut(el, () => renderLeaderboard());
  }
}

/* Popup mit dem öffentlichen Profil einer Person */
export async function publicProfileModal(id) {
  // Das Popup öffnet sofort in seiner späteren Größe – sonst ist es erst ein
  // schmaler Kasten und wächst dann ruckartig auf volle Höhe.
  modal({ title: 'Profil', okText: 'Schließen', body: skelProfilPopup(), onOk: () => {} });
  $('#modal-cancel').style.display = 'none';
  try {
    const { profile: p, decks, rel } = await cloudPublicProfile(id);
    const meta = [p.context, p.school].filter(Boolean).join(' · ');
    const decksHTML = decks.length
      ? `<div class="pp-decks">${decks.map((d) => {
          const avg = d.rating_count ? (d.rating_sum / d.rating_count).toFixed(1) : null;
          return `<div class="pp-deck"><b>${esc(d.title)}</b>${d.subject ? ` · <span class="page-sub">${esc(d.subject)}</span>` : ''}
            <div class="pp-deck-meta">🗂 ${d.card_count} · ❤ ${d.likes || 0} · ⬇ ${d.downloads || 0}${avg ? ` · ★ ${avg}` : ''}</div></div>`;
        }).join('')}</div>`
      : '<p class="page-sub">Noch keine öffentlichen Decks.</p>';
    $('#modal-body').innerHTML = `
      <div class="pp-head">
        ${catAvatar(p.cat_color, p.accessory, 'big')}
        <div class="pp-main">
          <div class="pp-name">${esc(p.name || p.username || 'Katze')} ${rel === 'friends' ? '<span class="pp-friend">✓ befreundet</span>' : ''}</div>
          ${meta ? `<div class="pp-meta">${esc(meta)}</div>` : ''}
          ${p.bio ? `<div class="pp-bio">${esc(p.bio)}</div>` : ''}
        </div>
      </div>
      <div class="pp-stats">
        <div class="pp-stat"><b>${p.level}</b><span>Level</span></div>
        <div class="pp-stat"><b>${p.xp}</b><span>XP</span></div>
        <div class="pp-stat"><b>${p.rated}</b><span>Karten</span></div>
        <div class="pp-stat"><b>${p.streak}</b><span>🔥 Streak</span></div>
      </div>
      <h4 class="dd-h">Öffentliche Decks</h4>
      ${decksHTML}`;
  } catch (e) {
    const body = $('#modal-body');
    body.innerHTML = fehlerBox('Das Profil konnte nicht geladen werden: ' + e.message);
    wireErneut(body, () => publicProfileModal(id));
  }
}

/* Klick auf ✎ → Feld direkt am Profil bearbeiten (Inline). */
export function wireProfileEditors() {
  $$('#view-profile [data-edit]').forEach((btn) => { btn.onclick = () => startInlineEdit(btn.dataset.edit); });
}
export function startInlineEdit(field) {
  const p = state.profile;
  const wrap = $(`#view-profile [data-edit="${field}"]`)?.closest('.pf-line, .pf-chip, #prof-bio, #prof-name');
  if (!wrap) return;
  let input;
  if (field === 'context') {
    input = document.createElement('select');
    input.className = 'pf-edit select';
    input.innerHTML = '<option value="">– keine –</option>' + CONTEXTS.map((c) => `<option ${p.context === c ? 'selected' : ''}>${c}</option>`).join('');
  } else if (field === 'bio') {
    input = document.createElement('textarea');
    input.className = 'pf-edit textarea'; input.rows = 3; input.maxLength = 240; input.value = p.bio || '';
  } else {
    input = document.createElement('input');
    input.className = 'pf-edit input';
    input.value = p[field] || '';
    if (field === 'age') { input.type = 'number'; input.min = 0; input.max = 120; input.placeholder = 'Alter'; }
    if (field === 'name') input.placeholder = 'Anzeigename';
    if (field === 'school') { input.placeholder = 'z.B. Universität Innsbruck'; input.setAttribute('list', 'uni-list'); }
  }
  wrap.innerHTML = '';
  wrap.appendChild(input);
  input.focus();
  if (input.select) input.select();
  const commit = async () => {
    let v = input.value;
    if (field === 'age') v = v.replace(/\D/g, '');
    p[field] = typeof v === 'string' ? v.trim() : v;
    /* Ab jetzt ist der Anzeigename selbst gewählt und wandert bei einer
       Umbenennung des Nutzernamens nicht mehr mit. */
    if (field === 'name') p.nameAuto = false;
    await saveProfile();
    renderProfilePreview();
    $('#pf-status').textContent = 'Gespeichert ✦'; $('#pf-status').className = 'status ok';
  };
  input.addEventListener('blur', commit, { once: true });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && field !== 'bio') { e.preventDefault(); input.blur(); }
    if (e.key === 'Escape') { input.removeEventListener('blur', commit); renderProfilePreview(); }
  });
  if (field === 'context') input.addEventListener('change', () => input.blur());
}
export function renderProfile() { renderProfilePreview(); renderProfileDecks(); }
export async function renderProfileDecks() {
  const box = $('#prof-decks'); if (!box) return;
  zeigeSkeleton(box, skelMeineDecks(2));
  try {
    const { decks } = await cloudMyDecks();
    ladend(box, false);
    if (!decks.length) { box.innerHTML = '<p class="page-sub">Noch nichts veröffentlicht. Geh auf <b>Community → „＋ Deck veröffentlichen"</b> (oder das ⋮-Menü an einem Kurs).</p>'; return; }
    box.innerHTML = `<div class="prof-deck-list">${decks.map((d) => `<div class="prof-deck">
      <b>${esc(d.title)}</b>${d.subject ? ` · <span class="page-sub">${esc(d.subject)}</span>` : ''}
      <div class="pd-meta"><span>🗂 ${d.card_count}</span><span>❤ ${d.likes || 0}</span><span>⬇ ${d.downloads || 0}</span><span>${d.visibility === 'friends' ? 'nur Freunde' : 'öffentlich'}</span></div>
    </div>`).join('')}</div>`;
  } catch (e) {
    zeigeInhalt(box, fehlerBox('Deine veröffentlichten Decks konnten nicht geladen werden: ' + e.message));
    wireErneut(box, () => renderProfileDecks());
  }
}

/* Echte deutsche Hochschulen/Unis für die Auswahl (Live-API + Cache + Fallback). */
export const UNI_FALLBACK = ['Universität Wien', 'Universität Innsbruck', 'MCI – Management Center Innsbruck', 'Technische Universität München', 'Ludwig-Maximilians-Universität München', 'Universität Heidelberg', 'Humboldt-Universität zu Berlin', 'RWTH Aachen', 'Universität Hamburg', 'Universität zu Köln', 'Goethe-Universität Frankfurt', 'Universität Stuttgart', 'Karlsruher Institut für Technologie', 'Universität Freiburg', 'Universität Münster', 'TU Berlin', 'TU Dresden', 'Universität Leipzig', 'Universität Bonn', 'Universität Göttingen'];
export function fillUniList() {
  const dl = $('#uni-list'); if (!dl) return;
  const list = state.unis.length ? state.unis : UNI_FALLBACK;
  dl.innerHTML = list.slice(0, 1200).map((n) => `<option value="${esc(n)}">`).join('');
}
/* Füllt die Vorschlagsliste für das Feld „Hochschule".

   Hier stand bis 12.08.2026 ein Aufruf an `universities.hipolabs.com` bei
   jedem App-Start. Zwei Gründe, warum er weg ist:

   • Der Dienst antwortet nicht mehr (ERR_CONNECTION_REFUSED). Jeder Start
     hing dadurch 2,5 Sekunden an einer Anfrage ins Leere.
   • Wichtiger: Er bekam bei jedem Start die IP-Adresse aller Nutzenden zu
     sehen – ein Dritter, der in der Datenschutzerklärung nicht auftaucht und
     mit dem es keine Vereinbarung gibt. Für eine Autovervollständigung ist
     das ein schlechtes Geschäft.

   UNI_FALLBACK deckt die gängigen deutschen Hochschulen ab, und das Feld ist
   ohnehin frei beschreibbar – wer eine seltenere Hochschule hat, tippt sie
   einfach aus. */
export function loadUnis() {
  fillUniList();
}


/* ══════════ Nutzernamen wählen (nach Google-/Apple-Registrierung) ══════════

   Wer sich mit E-Mail registriert, tippt seinen Namen selbst ein. Über einen
   Anbieter passiert das nie: Google liefert bestenfalls einen Vornamen, Apple
   oft gar nichts, und wenn dieser Name schon vergeben ist, hängt der Server
   eine Zufallszahl an. So entstand aus „Joey" ein „Joey582" – funktional
   richtig, aber nichts, was man sich aussucht.

   Deshalb fragt die App genau einmal nach: direkt nach der Registrierung,
   mit dem abgeleiteten Namen als Vorschlag. Wer damit leben kann, tippt
   „Passt so". Der Name ist öffentlich – unter ihm finden einen andere in der
   Freundessuche und der Bestenliste. */
export function askUsernameModal(user) {
  return new Promise((fertig) => {
  const vorschlag = (user && user.username) || '';
  const schliessen = modal({
    title: 'Wie sollen dich andere nennen?',
    okText: 'Übernehmen',
    body: `
      <p class="page-sub" style="margin-bottom:12px">
        Unter diesem Namen finden dich andere in der Freundessuche und in der
        Bestenliste. Wir haben einen aus deinem Konto abgeleitet – du kannst
        ihn jetzt ändern oder einfach so lassen.
      </p>
      <label class="field-lbl" for="uname-neu">Nutzername</label>
      <input class="input" id="uname-neu" maxlength="20" autocomplete="off"
             value="${esc(vorschlag)}" placeholder="z.B. Maira">
      <p class="page-sub" style="margin-top:6px;font-size:.78rem">
        3–20 Zeichen, beginnt mit einem Buchstaben, danach Buchstaben, Zahlen
        und Unterstriche. Keine Leerzeichen. Jeder Name gibt es nur einmal.
      </p>
      <p class="auth-status err" id="uname-fehler" role="status"></p>`,
    onOk: async () => {
      const feld = $('#uname-neu');
      const fehler = $('#uname-fehler');
      const wert = (feld.value || '').trim();

      // Unverändert gelassen? Dann gibt es nichts zu speichern.
      if (wert === vorschlag) return true;
      const formal = usernameProblem(wert);
      if (formal) { fehler.textContent = formal; return false; }   // Modal bleibt offen
      try {
        const d = await cloudSetUsername(wert);
        state.me = { ...state.me, username: d.user.username };
        await uebernehmeNutzername(d.user.username);
        const zeile = $('#account-line');
        if (zeile) zeile.textContent = `Angemeldet als ${d.user.username}${state.me.email ? ' · ' + state.me.email : ''}`;
        toast('Name gespeichert ✦');
        fertig(true);
        return true;
      } catch (err) {
        // Vergeben, zu viele Änderungen, offline – die Meldung kommt vom Server.
        fehler.textContent = err.message || 'Das hat nicht geklappt.';
        return false;
      }
    },
  });
  /* „Abbrechen" heißt hier nicht abbrechen, sondern „Vorschlag behalten" –
     der Name ist nie leer, es gibt also nichts zu erzwingen. */
  const abbrechen = $('#modal-cancel');
  if (abbrechen) {
    abbrechen.textContent = 'Passt so';
    abbrechen.onclick = () => { schliessen(); fertig(false); };
  }
  setTimeout(() => $('#uname-neu')?.select(), 60);
  });
}
